import { type FC, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  MenuItem,
  Typography,
  Grid,
  IconButton,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import DeleteIcon from '@mui/icons-material/Delete';
import type { PostgrestResponseSuccess } from '@supabase/postgrest-js';
import type {
  Merchant,
  MerchantCategory,
  MerchantSubcategory,
} from '../models/types';
import type { OverridableStringUnion } from '@mui/types';

import PageBaseLayout from '../layouts/PageBaseLayout';
import { DataContext } from '../contexts/DataContext';
import {
  createMerchant,
  getMerchantCategories,
  getMerchantSubcategories,
  updateMerchant,
} from '../fetchFns';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';

type FormValues = {
  name: string;
  email: string;
  category_id: string;
  subcategory_id: string;
  caption: string;
  logo: File[]; // Add images field
};

const defaultValues: FormValues = {
  name: '',
  email: '',
  category_id: '',
  subcategory_id: '',
  caption: '',
  logo: [],
};

const CreateNewMerchant: FC<{ updateMode?: boolean }> = ({
  updateMode = false,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    resetField,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
  });
  const dataCtx = useContext(DataContext);
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const [categories, setCategories] = useState<MerchantCategory[]>([]);
  const [subcategories, setSubcategories] = useState<MerchantSubcategory[]>([]);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const [logoImgFile, setLogoImgFile] = useState<File>();

  const logoWatch = watch('logo');
  const categoryWatch = watch('category_id');

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    console.log(files);
    if (!files || files.length === 0) return;

    const imgFile = files[0];
    const img = new window.Image();
    img.src = URL.createObjectURL(imgFile);
    img.onload = () => {
      console.log(`width: ${img.width}, height: ${img.height}`);
      // Validation - ensure file width and height are the same
      // if (img.width !== img.height) {
      if (false) {
        snackbar.handleSnackbarOpen(
          'Please ensure that the logo width and height are the same.',
          'error',
        );
        return;
        // Optionally, you could return here to prevent upload
      }
      const fileArray = Array.from(files);
      setValue('logo', fileArray, { shouldValidate: true });
      // Reset input value so same file can be selected again if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    img.onerror = () => {
      snackbar.handleSnackbarOpen('Error loading image.', 'error');
      return;
    };
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...(logoWatch || [])];
    newImages.splice(index, 1);
    setValue('logo', newImages, { shouldValidate: true });
  };

  const onSubmit = async (data: FormValues) => {
    // data.images is an array of File objects
    const newData = { ...data, logo: data.logo[0] };
    const formData = new FormData();
    backdrop.handleBackdropOpen();

    for (const [key, value] of Object.entries(newData)) {
      formData.append(key, value);
    }

    let message = `The brand has been successfully ${
      updateMode ? 'updated' : 'created'
    }.`;
    let severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    try {
      if (updateMode) {
        formData.append('merchant_id', location.state.id);

        // Validation - If the logo hasn't changed, delete the logo key to prevent re-upload
        if (logoImgFile === formData.get('logo')!.valueOf()) {
          formData.delete('logo');
        }

        const response = await updateMerchant(formData);
      } else {
        const response = await createMerchant(formData);
      }
      await navigate('/dashboard/merchants');
    } catch (error) {
      message = String(error);
      severity = 'error';
    }
    snackbar.handleSnackbarOpen(message, severity);
    backdrop.handleBackdropClose();
    return;

    await new Promise((resolve) => setTimeout(resolve, 1000));
    reset();
    dataCtx.handleSnackbarOpen('Campaign created successfully!', 'success');
  };

  const getData = async () => {
    backdrop.handleBackdropOpen();
    try {
      const responses = await Promise.allSettled([
        getMerchantCategories(),
        getMerchantSubcategories(),
      ]);

      if (responses.some((el) => el.status === 'rejected')) {
        const responseWithError = responses.find(
          (response) => response.status === 'rejected',
        );
        console.error(responseWithError?.reason);

        throw new Error('Error loading data.');
      }

      const responsesMapped = responses.map(
        (response) =>
          (response as PromiseFulfilledResult<PostgrestResponseSuccess<any>>)
            .value,
      );
      setCategories(responsesMapped[0].data);
      setSubcategories(responsesMapped[1].data);

      if (updateMode) {
        const merchantData: Merchant = location.state;
        try {
          const response = await fetch(location.state.logo_url);
          const blob = await response!.blob();
          const imgFile = new File([blob], '', { type: blob.type });
          setValue('logo', [imgFile]);
          setLogoImgFile(imgFile);
        } catch (error) {
          console.error((error as Error).stack);
          snackbar.handleSnackbarOpen('Error loading logo.', 'error');
        }

        setValue('name', merchantData.name);
        setValue('email', merchantData.email);
        setValue('category_id', merchantData.category_id.toString());
        setValue('subcategory_id', merchantData.subcategory_id.toString());
        setValue('caption', merchantData.caption);
      }

      // setValue('name', 'Giordano');
      // setValue('category_id', '2');
      // setValue('subcategory_id', '7');
      // setValue('caption', 'The best clothes');
      // setValue('logo', []);
    } catch (error) {
      const errorCast = error as Error;

      console.error(errorCast.stack);
      console.error(errorCast.name);
      snackbar.handleSnackbarOpen(String(error), 'error');
    }
    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <PageBaseLayout title={updateMode ? 'Update Brand' : 'Create New Brand'}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={3}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'Brand name is required',
              validate: (value) =>
                !value.includes('.') || 'Brand name cannot contain a period',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Brand Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                required
                fullWidth
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{ required: 'Email is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                error={!!errors.email}
                helperText={
                  errors.email?.message ??
                  'All official communications from Tranding will be sent to this email address'
                }
                required
                fullWidth
              />
            )}
          />
          <Controller
            name="category_id"
            control={control}
            rules={{ required: 'Campaign type is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(ev) => {
                  field.onChange(ev);
                  // Reset subcategory when category changes
                  resetField('subcategory_id');
                }}
                select
                label="Category"
                error={!!errors.category_id}
                helperText={errors.category_id?.message}
                required
                fullWidth>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.category}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="subcategory_id"
            disabled={!watch('category_id')}
            control={control}
            rules={{ required: 'Subcategory is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Subcategory"
                error={!!errors.subcategory_id}
                helperText={errors.subcategory_id?.message}
                required
                fullWidth>
                {subcategories
                  .filter((el) => el.merchant_category_id == categoryWatch)
                  .map((subcategory) => (
                    <MenuItem key={subcategory.id} value={subcategory.id}>
                      {subcategory.subcategory}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
          <Controller
            name="caption"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Caption"
                multiline
                rows={3}
                fullWidth
              />
            )}
          />

          {/* Image Upload Section */}
          <Controller
            name="logo"
            control={control}
            rules={{ required: 'Please select upload a logo image file' }}
            render={({ field, fieldState }) => (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Upload Logo
                </Typography>
                <Typography variant="body1" fontStyle="italic" gutterBottom>
                  *Please ensure that the logo width and height are the same.
                </Typography>
                <input
                  {...{ ...field, value: undefined }}
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                  data-testid="image-upload-input"
                />
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}>
                  Select Logo
                </Button>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {(logoWatch || []).map((file, idx) => (
                    <Grid key={file.name + idx}>
                      <Box
                        sx={{
                          position: 'relative',
                          width: 240,
                          height: 240,
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#fafafa',
                        }}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`preview-${idx}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(idx)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            background: 'rgba(255,255,255,0.7)',
                          }}
                          aria-label="Remove image">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {fieldState.error && (
                  <Typography color="error" variant="caption">
                    {fieldState.error.message}
                  </Typography>
                )}
              </Box>
              // <TextField
              //   {...field}
              //   label="Merchant Name"
              //   error={!!errors.name}
              //   helperText={errors.name?.message}
              //   required
              //   fullWidth
              // />
            )}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}>
            {updateMode ? 'Update' : 'Create Brand'}
          </Button>
        </Stack>
      </Box>
    </PageBaseLayout>
  );
};

export default CreateNewMerchant;
